import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { useMapData, type MapRegion } from "@/hooks/dashboard/use-map-data";
import { useAfricanAirportsData } from "@/hooks/dashboard/use-african-airports-data";
import { findAirportByCity, findCountryByCity } from "@/utils/cities/airport-lookup";
import {
  canonicalizeCountryName,
  getFeatureCommonName,
} from "@/utils/countries/africa";

interface FlowMapWidgetProps {
  data: {
    type: "flow-map";
    title: string;
    connections: Array<{
      city1: string;
      city2: string;
      value: number;
      city1Country: string;
      city2Country: string;
      city1Lat?: number;
      city1Lng?: number;
      city2Lat?: number;
      city2Lng?: number;
    }>;
    primaryCities: string[];
    meta: any;
    empty: boolean;
  };
  widget: any;
}

export default function FlowMapWidget({ data, widget }: FlowMapWidgetProps) {
  const { t } = useTranslation();
  const [selectedPrimaryCities, setSelectedPrimaryCities] = useState<Set<string>>(new Set());
  const [hoveredConnection, setHoveredConnection] = useState<{
    city1: string;
    city2: string;
    city1Country: string;
    city2Country: string;
    value: number;
    x: number;
    y: number;
  } | null>(null);
  const [hoveredCity, setHoveredCity] = useState<{
    city: string;
    country: string;
    x: number;
    y: number;
  } | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

  const mapOptions = widget?.config?.options?.flowMap || {};
  const region = mapOptions?.region || "africa";
  const appearance = mapOptions?.appearance || {};
  const showLegend = !!appearance?.showLegend;
  const legendLabel = appearance?.legendLabel || "Value";
  const legendLocation = appearance?.legendLocation || "bottom-center";
  const primaryCityColor = appearance?.primaryCityColor || "#ef4444";
  const secondaryCityColor = appearance?.secondaryCityColor || "#06b6d4";
  const lineColor = appearance?.lineColor || "#3b82f6";
  const showCityName = appearance?.showCityName !== false;
  const primaryCityNameColor = appearance?.primaryCityNameColor || "#ffffff";
  const secondaryCityNameColor = appearance?.secondaryCityNameColor || "#ffffff";
  const showValueTooltip = appearance?.showValueTooltip !== false;
  const showArrowHead = appearance?.showArrowHead === true;
  const countryFillColor = appearance?.countryFillColor || "#e5e7eb";
  const countryHoverColor = appearance?.countryHoverColor || "#d1d5db";
  const countryBorderColor = appearance?.countryBorderColor || "#d1d5db";
  const showSubtitle = !!appearance?.showSubtitle;
  const subtitle = appearance?.subtitle || "";

  const { data: geoData, loading: geoDataLoading, error: geoDataError } = useMapData(region as MapRegion);
  const { data: airportsData, loading: airportsLoading, error: airportsError } = useAfricanAirportsData();

  // Enrich connections with coordinates and country names
  const enrichedConnections = useMemo(() => {
    if (!data.connections || data.connections.length === 0) return [];
    const shouldUseAirportData = region === "africa" && airportsData && !airportsLoading;

    return data.connections.map((conn) => {
      let city1Lat = conn.city1Lat;
      let city1Lng = conn.city1Lng;
      let city2Lat = conn.city2Lat;
      let city2Lng = conn.city2Lng;
      let city1Country = conn.city1Country;
      let city2Country = conn.city2Country;

      // Lookup coordinates and country if not provided
      if (shouldUseAirportData) {
        if (!city1Lat || !city1Lng) {
          const airport1 = findAirportByCity(conn.city1, airportsData);
          if (airport1) {
            city1Lat = parseFloat(airport1.latitude);
            city1Lng = parseFloat(airport1.longitude);
          }
        }
        if (!city2Lat || !city2Lng) {
          const airport2 = findAirportByCity(conn.city2, airportsData);
          if (airport2) {
            city2Lat = parseFloat(airport2.latitude);
            city2Lng = parseFloat(airport2.longitude);
          }
        }

        // Lookup country names if they are missing or "Unknown"
        if (!city1Country || city1Country === "Unknown") {
          const country1 = findCountryByCity(conn.city1, airportsData);
          if (country1) {
            city1Country = country1;
          }
        }
        if (!city2Country || city2Country === "Unknown") {
          const country2 = findCountryByCity(conn.city2, airportsData);
          if (country2) {
            city2Country = country2;
          }
        }
      }

      return {
        ...conn,
        city1Lat: city1Lat || null,
        city1Lng: city1Lng || null,
        city2Lat: city2Lat || null,
        city2Lng: city2Lng || null,
        city1Country: city1Country || "Unknown",
        city2Country: city2Country || "Unknown",
      };
    }).filter((conn) =>
      conn.city1Lat !== null &&
      conn.city1Lng !== null &&
      conn.city2Lat !== null &&
      conn.city2Lng !== null
    );
  }, [data.connections, region, airportsData, airportsLoading]);

  // Get all unique cities from connections
  const allCities = useMemo(() => {
    const citySet = new Set<string>();
    enrichedConnections.forEach((conn) => {
      citySet.add(conn.city1);
      citySet.add(conn.city2);
    });
    return Array.from(citySet);
  }, [enrichedConnections]);

  // Get city coordinates map
  const cityCoordinates = useMemo(() => {
    const coords: Record<string, { lat: number; lng: number; country: string }> = {};
    enrichedConnections.forEach((conn) => {
      if (conn.city1Lat && conn.city1Lng) {
        coords[conn.city1] = { lat: conn.city1Lat, lng: conn.city1Lng, country: conn.city1Country };
      }
      if (conn.city2Lat && conn.city2Lng) {
        coords[conn.city2] = { lat: conn.city2Lat, lng: conn.city2Lng, country: conn.city2Country };
      }
    });
    return coords;
  }, [enrichedConnections]);

  // Get visible connections (only for selected primary cities)
  const visibleConnections = useMemo(() => {
    if (selectedPrimaryCities.size === 0) return [];
    return enrichedConnections.filter((conn) =>
      selectedPrimaryCities.has(conn.city1) || selectedPrimaryCities.has(conn.city2)
    );
  }, [enrichedConnections, selectedPrimaryCities]);

  // Get visible secondary cities (cities connected to selected primary cities)
  const visibleSecondaryCities = useMemo(() => {
    if (selectedPrimaryCities.size === 0) return new Set<string>();
    const secondary = new Set<string>();
    visibleConnections.forEach((conn) => {
      if (!selectedPrimaryCities.has(conn.city1)) {
        secondary.add(conn.city1);
      }
      if (!selectedPrimaryCities.has(conn.city2)) {
        secondary.add(conn.city2);
      }
    });
    return secondary;
  }, [visibleConnections, selectedPrimaryCities]);

  // Calculate max value for line thickness
  const maxValue = useMemo(() => {
    if (enrichedConnections.length === 0) return 1;
    return Math.max(...enrichedConnections.map((c) => c.value), 1);
  }, [enrichedConnections]);

  const getLineThickness = (value: number) => {
    const minThickness = 1;
    const maxThickness = 8;
    const normalized = maxValue > 0 ? value / maxValue : 0;
    return minThickness + (maxThickness - minThickness) * normalized;
  };

  // Calculate curved path for connection
  // Note: react-simple-maps uses [longitude, latitude] format
  const getCurvedPath = (lat1: number, lng1: number, lat2: number, lng2: number): string => {
    // Calculate midpoint
    const midLat = (lat1 + lat2) / 2;
    const midLng = (lng1 + lng2) / 2;

    // Calculate distance (in degrees, approximate)
    const distance = Math.sqrt(
      Math.pow(lat2 - lat1, 2) + Math.pow(lng2 - lng1, 2)
    );

    // Calculate perpendicular offset (slight curve, 5-10% of distance)
    const offsetPercent = 0.08; // 8% offset for slight curve
    const offset = distance * offsetPercent;

    // Calculate perpendicular direction
    const angle = Math.atan2(lat2 - lat1, lng2 - lng1);
    const perpAngle = angle + Math.PI / 2;

    // Calculate control point
    const controlLat = midLat + offset * Math.sin(perpAngle);
    const controlLng = midLng + offset * Math.cos(perpAngle);

    // Create quadratic bezier curve (coordinates are [lng, lat] for SVG path)
    return `M ${lng1} ${lat1} Q ${controlLng} ${controlLat} ${lng2} ${lat2}`;
  };

  // Handle primary city click
  const handlePrimaryCityClick = useCallback((city: string) => {
    setSelectedPrimaryCities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(city)) {
        newSet.delete(city);
      } else {
        newSet.add(city);
      }
      return newSet;
    });
  }, []);

  // Handle map click (deselect if clicking empty area)
  const handleMapClick = useCallback((e: React.MouseEvent) => {
    // Only deselect if clicking directly on the map background, not on cities or lines
    if ((e.target as HTMLElement).tagName === 'svg' ||
        (e.target as HTMLElement).closest('path[data-geography]')) {
      setSelectedPrimaryCities(new Set());
    }
  }, []);

  // Get legend position classes
  const getLegendPositionClasses = () => {
    const positions: Record<string, string> = {
      "top-left": "top-1 left-1 sm:top-2 sm:left-2",
      "top-center": "top-1 left-1/2 -translate-x-1/2 sm:top-2",
      "top-right": "top-1 right-1 sm:top-2 sm:right-2",
      "bottom-left": "bottom-1 left-1 sm:bottom-2 sm:left-2",
      "bottom-center": "bottom-1 left-1/2 -translate-x-1/2 sm:bottom-2",
      "bottom-right": "bottom-1 right-1 sm:bottom-2 sm:right-2",
    };
    return positions[legendLocation] || positions["bottom-center"];
  };

  // Calculate legend values for line thickness
  const legendValues = useMemo(() => {
    if (enrichedConnections.length === 0 || maxValue === 0) return [];
    const minValue = Math.min(...enrichedConnections.map((c) => c.value));
    const steps = 4;
    const values: number[] = [];
    for (let i = 0; i <= steps; i++) {
      const value = minValue + (maxValue - minValue) * (i / steps);
      values.push(Math.round(value));
    }
    return values.filter((v, i, arr) => i === 0 || v !== arr[i - 1]);
  }, [enrichedConnections, maxValue]);

  if (geoDataLoading || (region === "africa" && airportsLoading)) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('forms.loadingMapData')}</div>
      </div>
    );
  }

  if (geoDataError || (region === "africa" && airportsError)) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-red-500">
          {t('forms.errorLoadingMapData', {
            error: geoDataError || airportsError || 'Unknown error'
          })}
        </div>
      </div>
    );
  }

  if (!geoData) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('forms.mapDataNotAvailable')}</div>
      </div>
    );
  }

  if (data.empty || enrichedConnections.length === 0) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('processManagement.noDataForSelectedCriteria')}</div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full flex flex-col">
      {showSubtitle && subtitle && (
        <div className="flex-none px-2 py-1 text-xs sm:text-sm text-gray-700 font-medium border-b text-center">
          {subtitle}
        </div>
      )}
      <div
        className="flex-1 relative"
        onMouseMove={(e) => {
          if (hoveredConnection || hoveredCity) {
            const rect = e.currentTarget.getBoundingClientRect();
            setMousePosition({
              x: e.clientX - rect.left,
              y: e.clientY - rect.top,
            });
          }
        }}
        onMouseLeave={() => {
          setHoveredConnection(null);
          setHoveredCity(null);
          setMousePosition(null);
        }}
        onClick={handleMapClick}
      >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: region === "world" ? 100 : 600,
          center: region === "world" ? [0, 0] : [20, 0]
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup center={[0, 0]} maxZoom={40} zoom={1}>
          {/* Arrow marker definition for directional connections */}
          {showArrowHead && (
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L0,6 L9,3 z"
                  fill={lineColor}
                  opacity={0.7}
                />
              </marker>
            </defs>
          )}
          <Geographies geography={geoData}>
            {({ geographies, projection }) => {
              // Calculate projected paths for connections
              const projectedPaths = visibleConnections.map((conn) => {
                if (!conn.city1Lat || !conn.city1Lng || !conn.city2Lat || !conn.city2Lng) return null;

                // Project coordinates to screen space
                const point1 = projection([conn.city1Lng, conn.city1Lat]);
                const point2 = projection([conn.city2Lng, conn.city2Lat]);

                if (!point1 || !point2) return null;

                // Calculate midpoint
                const midX = (point1[0] + point2[0]) / 2;
                const midY = (point1[1] + point2[1]) / 2;

                // Calculate distance
                const distance = Math.sqrt(
                  Math.pow(point2[0] - point1[0], 2) + Math.pow(point2[1] - point1[1], 2)
                );

                // Calculate perpendicular offset (slight curve, 8% of distance)
                const offsetPercent = 0.08;
                const offset = distance * offsetPercent;

                // Calculate perpendicular direction
                const angle = Math.atan2(point2[1] - point1[1], point2[0] - point1[0]);
                const perpAngle = angle + Math.PI / 2;

                // Calculate control point
                const controlX = midX + offset * Math.cos(perpAngle);
                const controlY = midY + offset * Math.sin(perpAngle);

                // Create quadratic bezier curve
                const path = `M ${point1[0]} ${point1[1]} Q ${controlX} ${controlY} ${point2[0]} ${point2[1]}`;

                return {
                  path,
                  conn,
                  thickness: getLineThickness(conn.value),
                };
              }).filter(Boolean);

              return (
                <>
                  {geographies.map((geo) => {
                    const featureNameRaw = getFeatureCommonName(geo.properties, region);
                    const key = canonicalizeCountryName(featureNameRaw);

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default: {
                            fill: countryFillColor,
                            outline: "none",
                        stroke: countryBorderColor,
                        strokeWidth: 0.3,
                          },
                          hover: {
                            fill: countryHoverColor,
                            outline: "none",
                          },
                          pressed: {
                            fill: countryHoverColor,
                            outline: "none",
                          },
                        }}
                      />
                    );
                  })}

                  {/* Render connections (curved lines) */}
                  <g>
                    {projectedPaths.map((item: any, idx: number) => {
                      if (!item) return null;
                      const { path, conn, thickness } = item;

                      return (
                        <path
                          key={idx}
                          d={path}
                          fill="none"
                          stroke={lineColor}
                          strokeWidth={thickness}
                          strokeOpacity={0.7}
                          {...(showArrowHead && { markerEnd: "url(#arrowhead)" })}
                          style={{ cursor: showValueTooltip ? 'pointer' : 'default' }}
                          onMouseEnter={() => {
                            if (showValueTooltip) {
                              setHoveredConnection({
                                city1: conn.city1,
                                city2: conn.city2,
                                city1Country: conn.city1Country,
                                city2Country: conn.city2Country,
                                value: conn.value,
                                x: 0,
                                y: 0,
                              });
                            }
                          }}
                          onMouseLeave={() => {
                            if (showValueTooltip) {
                              setHoveredConnection(null);
                            }
                          }}
                        />
                      );
                    })}
                  </g>
                </>
              );
            }}
          </Geographies>

          {/* Render secondary cities (only when connections visible) - rendered first so primary cities appear on top */}
          {Array.from(visibleSecondaryCities).map((city, idx) => {
            const coords = cityCoordinates[city];
            if (!coords) return null;

            return (
              <Marker key={`secondary-${idx}`} coordinates={[coords.lng, coords.lat]}>
                <g
                  onMouseEnter={() => {
                    setHoveredCity({
                      city: city,
                      country: coords.country,
                      x: 0,
                      y: 0,
                    });
                  }}
                  onMouseLeave={() => {
                    setHoveredCity(null);
                  }}
                >
                  <circle
                    r={5}
                    fill={secondaryCityColor}
                    stroke="#ffffff"
                    strokeWidth={1.5}
                  />
                  {showCityName && (
                    <text
                      textAnchor="middle"
                      y={-10}
                      style={{
                        fontFamily: "system-ui, sans-serif",
                        fontSize: "11px",
                        fill: secondaryCityNameColor,
                        fontWeight: "500",
                        textShadow: "0 1px 2px rgba(0,0,0,0.7)",
                        pointerEvents: "none",
                      }}
                    >
                      {city}
                    </text>
                  )}
                </g>
              </Marker>
            );
          })}

          {/* Render primary cities (always visible) - rendered last so they appear on top */}
          {data.primaryCities.map((city, idx) => {
            const coords = cityCoordinates[city];
            if (!coords) return null;

            const isSelected = selectedPrimaryCities.has(city);
            const markerColor = primaryCityColor;
            const nameColor = primaryCityNameColor;

            return (
              <Marker key={`primary-${idx}`} coordinates={[coords.lng, coords.lat]}>
                <g
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrimaryCityClick(city);
                  }}
                  onMouseEnter={() => {
                    setHoveredCity({
                      city: city,
                      country: coords.country,
                      x: 0,
                      y: 0,
                    });
                  }}
                  onMouseLeave={() => {
                    setHoveredCity(null);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <circle
                    r={isSelected ? 10 : 8}
                    fill={markerColor}
                    stroke="#ffffff"
                    strokeWidth={2}
                  />
                  {showCityName && (
                    <text
                      textAnchor="middle"
                      y={-12}
                      style={{
                        fontFamily: "system-ui, sans-serif",
                        fontSize: "12px",
                        fill: nameColor,
                        fontWeight: "600",
                        textShadow: "0 1px 2px rgba(0,0,0,0.7)",
                        pointerEvents: "none",
                      }}
                    >
                      {city}
                    </text>
                  )}
                </g>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Legend */}
      {showLegend && legendValues.length > 0 && (
        <div className={`absolute ${getLegendPositionClasses()} bg-white bg-opacity-95 p-2 sm:p-3 md:p-4 rounded-lg shadow-lg z-10 max-w-[calc(100%-1rem)] sm:max-w-none`}>
          <div className="text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 md:mb-3 text-gray-800 truncate">{legendLabel}</div>
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-wrap">
            {legendValues.map((value, idx) => {
              const thickness = getLineThickness(value);
              return (
                <div key={idx} className="flex items-center gap-1 sm:gap-2">
                  <div
                    style={{
                      width: "30px",
                      height: `${thickness}px`,
                      backgroundColor: lineColor,
                      opacity: 0.7,
                      flexShrink: 0,
                    }}
                    className="sm:w-[40px]"
                  />
                  <span className="text-[10px] sm:text-xs text-gray-700 font-medium whitespace-nowrap">
                    {value.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Connection Tooltip */}
      {showValueTooltip && hoveredConnection && mousePosition && (
        <div
          className="absolute bg-gray-900 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded shadow-lg z-20 pointer-events-none max-w-[200px] sm:max-w-none"
          style={{
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y - 10}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="text-xs sm:text-sm font-semibold truncate">
            {hoveredConnection.city1Country && hoveredConnection.city1Country !== 'Unknown'
              ? `${hoveredConnection.city1Country}/${hoveredConnection.city1}`
              : hoveredConnection.city1} → {hoveredConnection.city2Country && hoveredConnection.city2Country !== 'Unknown'
              ? `${hoveredConnection.city2Country}/${hoveredConnection.city2}`
              : hoveredConnection.city2}
          </div>
          <div className="text-[10px] sm:text-xs">{legendLabel}: {hoveredConnection.value.toLocaleString()}</div>
        </div>
      )}

      {/* City Marker Tooltip */}
      {hoveredCity && mousePosition && (
        <div
          className="absolute bg-gray-900 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded shadow-lg z-20 pointer-events-none max-w-[200px] sm:max-w-none"
          style={{
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y - 10}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="text-xs sm:text-sm font-semibold truncate">
            {hoveredCity.country && hoveredCity.country !== 'Unknown'
              ? `${hoveredCity.country}/${hoveredCity.city}`
              : hoveredCity.city}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

